import { useCallback, useEffect, useRef, useState } from 'react'
import {
  api,
  can,
  commitAccepted,
  importSource,
  refusalReason,
  saveFile,
  shortDate,
  type ImportBatch,
  type ImportDraftSummary,
  type ImportMappingListed,
  type ImportRestoreResult,
  type ImportUndoResult,
} from '../lib/api'
import { importingWords, IMPORT_STEPS, plural } from '../lib/importWords'
import { importAddress } from '../lib/importDraft'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { ImportDocuments } from '../components/ImportDocuments'
import { CountReportButton, ImportError, RestoreForm, RestoreSaid, UndoForm, UndoSaid } from '../components/ImportUndo'
import { PageTabs, Status, type PageTab, type Tone } from '../components/kit'
import { useToast } from '../components/ui'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { FOLLOW_MS, resultHeadline } from './Import'
import { mappingName, MAPPING_NAME_MAX } from './import/columnTiers'
import '../styles/sweep.css'
import '../styles/import.css'
import './import/history.css'

// Import history (release 3.3, I5; plan 4.7 and 4.8).
//
// # ONE IMPORTER, ONE LIST
//
// This page was "Imports": a form to stage a file through the older pipeline
// (a source system, an entity, a profile), the profiles it read with, and the
// staged rows with a commit bar under them. Release 3.2 made the import
// wizard the one way a file comes in, and the staging form became a second
// importer that said different things about the same file. It is retired:
// "Import a file" opens the wizard. What stays is the history (file, who,
// when, counts, status; Undo, Restore, Resume, the report and the contracts),
// and two tabs beside it:
//
//  - In progress: the imports a person left part way (I1's draft list):
//    whose, which step, when. Continue opens the step it was on; another
//    person's opens read only. Discard is for its owner or a holder of
//    vendor.import_admin, the same rule the API enforces.
//  - Saved mappings: the column choices, value answers and duplicate rule
//    saved under a name, so the next file with the same columns arrives
//    matched. Rename, copy, delete and export. A mapping an import used is
//    never deleted: the API refuses with the count, and that is what is said.
//
// The tab is in the address (#/imports?show=in-progress), so a link and a
// refresh open the same part.

// The statuses an import moves through, as external_imports stores them.
const BATCH_STATUS: Record<string, { tone: Tone; word: string }> = {
  RECEIVED: { tone: 'neutral', word: 'Received' },
  PROFILING: { tone: 'info', word: 'In progress' },
  STAGED: { tone: 'info', word: 'In progress' },
  REVIEWING: { tone: 'info', word: 'In progress' },
  COMMITTING: { tone: 'info', word: 'Importing' },
  COMMITTED: { tone: 'plain', word: 'Imported' },
  REJECTED: { tone: 'neutral', word: 'Rejected' },
  FAILED: { tone: 'crit', word: 'Failed' },
}

// A batch still marked as being brought in whose commit stopped part way (a
// restart of the server) and that nobody is writing: it can be carried on or
// undone, and says so rather than "being brought in" for ever.
const STOPPED_PART_WAY = { tone: 'warn' as Tone, word: 'Stopped part way' }

export function batchStatus(b: ImportBatch): { tone: Tone; word: string } | undefined {
  if (b.status === 'COMMITTING' && b.canResume && !b.undoneAt) return STOPPED_PART_WAY
  return BATCH_STATUS[b.status]
}

// A status this file does not know is still printed, in sentence case.
function words(code: string): string {
  return code.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// ------------------------------------------------------------- the tabs

export type HistoryTab = 'imports' | 'in-progress' | 'mappings'

const TAB_ADDRESS: Record<HistoryTab, string> = {
  imports: '#/imports',
  'in-progress': '#/imports?show=in-progress',
  mappings: '#/imports?show=mappings',
}

// The tab an address asks for. Saved mappings are offered only to a person
// who may read them (vendor.import or config.read), so an address naming it
// opens the history for anybody else.
export function historyTab(hash: string, mayReadMappings: boolean): HistoryTab {
  const show = new URLSearchParams(hash.split('?')[1] ?? '').get('show')
  if (show === 'in-progress') return 'in-progress'
  if (show === 'mappings' && mayReadMappings) return 'mappings'
  return 'imports'
}

// ------------------------------------------------------ imports in progress

// "Step 3 of 5, Match values".
export function stepWords(d: Pick<ImportDraftSummary, 'stepNumber' | 'steps'>): string {
  const n = Math.min(Math.max(d.stepNumber, 1), IMPORT_STEPS.length)
  return 'Step ' + n + ' of ' + (d.steps || IMPORT_STEPS.length) + ', ' + IMPORT_STEPS[n - 1]
}

// What a person may do with an import in progress: open it at its step (their
// own to carry on, another person's to read: both need vendor.import, which
// reading a draft needs), and discard it (its owner, or vendor.import_admin).
export function draftActions(
  d: Pick<ImportDraftSummary, 'yours'>,
  held: { mayImport: boolean; importAdmin: boolean },
): { open: '' | 'Continue' | 'Open'; discard: boolean } {
  return {
    open: !held.mayImport ? '' : d.yours ? 'Continue' : 'Open',
    discard: d.yours ? held.mayImport : held.importAdmin,
  }
}

// Where the focus goes when what had it is gone (I5 review nits, 13 Oct
// 2026): a question opened puts it on the question's answer, a question
// closed back on the button that asked it, a saved name back on Rename, and a
// row that went on the list's tab. Moved once the change is drawn; left
// alone if the reader has moved it since.
export function focusLater(find: () => HTMLElement | null | undefined, from: Element | null = document.activeElement): void {
  window.setTimeout(() => {
    const at = document.activeElement
    if (at !== from && at !== document.body && at !== null && document.contains(at)) return
    find()?.focus()
  }, 0)
}
// The buttons the focus is sent back to, held by a key of the screen's own
// (no id in the page: a mapping's or a draft's id is never drawn).
function useFocusTargets() {
  const els = useRef(new Map<string, HTMLElement>())
  return {
    keep: (key: string) => (el: HTMLElement | null) => {
      if (el) els.current.set(key, el)
      else els.current.delete(key)
    },
    byKey: (key: string) => () => els.current.get(key),
  }
}
const shownTab = () => document.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')

// The Discard question, naming whose draft it is when it is not the reader's.
export function discardAsk(d: Pick<ImportDraftSummary, 'fileName' | 'yours' | 'ownerName'>): string {
  const what = d.yours || !d.ownerName ? d.fileName : d.ownerName + "'s " + d.fileName
  return 'Discard ' + what + '? Nothing from it was imported, and it can no longer be.'
}

export function InProgress({
  drafts,
  keptForDays,
  onChanged,
}: {
  drafts: ImportDraftSummary[]
  keptForDays: number
  onChanged: () => void
}) {
  const toast = useToast()
  const held = { mayImport: can('vendor.import'), importAdmin: can('vendor.import_admin') }
  const { keep, byKey } = useFocusTargets()
  const [asking, setAsking] = useState('')
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState<{ id: string; error: unknown } | null>(null)

  async function discard(d: ImportDraftSummary) {
    setBusy(d.importId)
    setSaid(null)
    try {
      const r = await api.discardImportDraft(d.importId)
      toast(r.note || 'Discarded ' + d.fileName + '.')
      setAsking('')
      onChanged()
      focusLater(shownTab)
    } catch (err) {
      setSaid({ id: d.importId, error: err })
    } finally {
      setBusy('')
    }
  }

  const columns: Column<ImportDraftSummary>[] = [
    {
      key: 'file',
      header: 'File',
      sortValue: (d) => d.fileName,
      render: (d) => (
        <>
          <strong className="imw-hname">{d.fileName || 'Unnamed file'}</strong>
          <span className="cell-sub">{plural(d.rows, 'row')}</span>
        </>
      ),
    },
    { key: 'who', header: 'Whose', sortValue: (d) => (d.yours ? '' : d.ownerName), render: (d) => (d.yours ? 'Yours' : d.ownerName) },
    { key: 'step', header: 'Step', sortValue: (d) => d.stepNumber, render: (d) => stepWords(d) },
    { key: 'updated', header: 'Last change', sortValue: (d) => d.updatedAt, render: (d) => shortDate(d.updatedAt) },
    {
      key: 'act',
      header: 'Actions',
      render: (d) => {
        const may = draftActions(d, held)
        return (
          <>
            <span className="imw-batch-acts">
              {may.open && (
                <a className={'button' + (d.yours ? ' primary' : '')} href={importAddress(d.importId, d.stepNumber)}>
                  {may.open}
                  <span className="vh"> {d.fileName}</span>
                </a>
              )}
              {may.discard && asking !== d.importId && (
                <button
                  type="button"
                  ref={keep('discard:' + d.importId)}
                  onClick={() => {
                    setAsking(d.importId)
                    focusLater(byKey('discard-yes:' + d.importId))
                  }}
                >
                  Discard<span className="vh"> {d.fileName}</span>
                </button>
              )}
              {!may.discard && !d.yours && held.mayImport && (
                <ReasonTip
                  label={'Why you cannot discard ' + (d.fileName || 'this import')}
                  text={'Only ' + d.ownerName + ' or a person who may manage imports can discard it.'}
                />
              )}
            </span>
            {asking === d.importId && (
              <span className="imw-ask" role="group" aria-label={'Discard ' + d.fileName}>
                {/* Another person's draft says whose it is: discarding it throws
                    away their work, not the reader's. */}
                <span>{discardAsk(d)}</span>
                <button type="button" ref={keep('discard-yes:' + d.importId)} className="danger" disabled={busy !== ''} onClick={() => void discard(d)}>
                  {busy === d.importId ? 'Discarding...' : 'Discard'}
                </button>
                <button
                  type="button"
                  disabled={busy !== ''}
                  onClick={() => {
                    setAsking('')
                    focusLater(byKey('discard:' + d.importId))
                  }}
                >
                  Keep it
                </button>
              </span>
            )}
            {said?.id === d.importId && <ImportError error={said.error} />}
          </>
        )
      },
    },
  ]

  return (
    <DataTable
      caption="Imports in progress: whose, which step, and when it last changed."
      columns={columns}
      rows={drafts}
      rowKey={(d) => d.importId}
      defaultSort={{ key: 'updated', dir: 'desc' }}
      firstRows={25}
      empty={{
        title: 'No import is in progress',
        detail: 'An import left part way shows here, and opens on the step it was on.',
        action: held.mayImport ? { label: 'Import a file', href: '#/import' } : undefined,
      }}
      footer={
        <ListCount shown={drafts.length} noun="imports in progress" one="import in progress">
          . Each is kept {plural(keptForDays, 'day')} after its last change
        </ListCount>
      }
    />
  )
}

// ----------------------------------------------------------- saved mappings

// What Delete asks before it sends: a mapping used before is said to be kept
// when an import that was brought in used it (the API's rule and count).
export function deleteAsk(m: Pick<ImportMappingListed, 'name' | 'timesUsed'>): string {
  if (m.timesUsed > 0) {
    return 'Delete ' + m.name + '? It was used ' + plural(m.timesUsed, 'time') + '. A mapping a finished import used is kept.'
  }
  return 'Delete ' + m.name + '? No import used it.'
}

export function usedWords(m: Pick<ImportMappingListed, 'timesUsed' | 'lastUsedAt'>): string {
  if (m.timesUsed === 0) return 'Not used yet'
  return 'Used ' + plural(m.timesUsed, 'time') + (m.lastUsedAt ? ', last on ' + shortDate(m.lastUsedAt) : '')
}

export function SavedMappings({ mappings, onChanged }: { mappings: ImportMappingListed[]; onChanged: () => void }) {
  const toast = useToast()
  const mayChange = can('vendor.import')
  const refusal = mayChange ? undefined : refusalReason('vendor.import')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const { keep, byKey } = useFocusTargets()
  const [asking, setAsking] = useState('')
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState<{ id: string; error: unknown } | null>(null)

  async function run(id: string, what: string, f: () => Promise<void>) {
    setBusy(id + what)
    setSaid(null)
    try {
      await f()
    } catch (err) {
      setSaid({ id, error: err })
    } finally {
      setBusy('')
    }
  }

  const rename = (m: ImportMappingListed, name: string) =>
    run(m.id, 'rename', async () => {
      await api.renameImportMapping(m.id, mappingName(name))
      setRenaming(null)
      onChanged()
      focusLater(byKey('rename:' + m.id))
    })
  const copy = (m: ImportMappingListed) =>
    run(m.id, 'copy', async () => {
      const c = await api.duplicateImportMapping(m.id)
      toast('Copied as ' + c.name + '.')
      onChanged()
    })
  const remove = (m: ImportMappingListed) =>
    run(m.id, 'delete', async () => {
      await api.deleteImportMapping(m.id)
      toast('Deleted ' + m.name + '.')
      setAsking('')
      onChanged()
      focusLater(shownTab)
    })
  const exportIt = (m: ImportMappingListed) =>
    run(m.id, 'export', async () => {
      saveFile(await api.exportImportMapping(m.id))
    })

  const columns: Column<ImportMappingListed>[] = [
    {
      key: 'name',
      header: 'Name',
      sortValue: (m) => m.name,
      render: (m) =>
        renaming?.id === m.id ? (
          <form
            className="imw-rename"
            onSubmit={(e) => {
              e.preventDefault()
              if (mappingName(renaming.name)) void rename(m, renaming.name)
            }}
          >
            <label className="vh" htmlFor={'imw-rename-' + m.id}>
              New name for {m.name}
            </label>
            <input
              id={'imw-rename-' + m.id}
              type="text"
              value={renaming.name}
              maxLength={MAPPING_NAME_MAX}
              autoFocus
              onChange={(e) => setRenaming({ id: m.id, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setRenaming(null)
                  focusLater(byKey('rename:' + m.id))
                }
              }}
            />
            <button type="submit" className="primary" disabled={busy !== '' || !mappingName(renaming.name)}>
              {busy === m.id + 'rename' ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRenaming(null)
                focusLater(byKey('rename:' + m.id))
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <>
            <strong className="imw-hname">{m.name}</strong>
            <span className="cell-sub">{m.source}</span>
          </>
        ),
    },
    {
      key: 'columns',
      header: 'Columns',
      sortValue: (m) => m.columns,
      render: (m) => m.matched + ' of ' + m.columns + ' matched',
    },
    { key: 'used', header: 'Used', sortValue: (m) => m.timesUsed, render: (m) => usedWords(m) },
    { key: 'changed', header: 'Last change', sortValue: (m) => m.updatedAt, render: (m) => shortDate(m.updatedAt) },
    {
      key: 'act',
      header: 'Actions',
      render: (m) => (
        <>
          <span className="imw-batch-acts">
            <button
              type="button"
              ref={keep('rename:' + m.id)}
              disabled={!mayChange || busy !== ''}
              title={refusal}
              onClick={() => {
                setAsking('')
                setRenaming({ id: m.id, name: m.name })
              }}
            >
              Rename<span className="vh"> {m.name}</span>
            </button>
            <button type="button" disabled={!mayChange || busy !== ''} title={refusal} onClick={() => void copy(m)}>
              {busy === m.id + 'copy' ? 'Copying...' : 'Copy'}
              <span className="vh"> {m.name}</span>
            </button>
            <button
              type="button"
              ref={keep('delete:' + m.id)}
              disabled={!mayChange || busy !== ''}
              title={refusal}
              onClick={() => {
                setRenaming(null)
                setAsking(m.id)
                focusLater(byKey('delete-yes:' + m.id))
              }}
            >
              Delete<span className="vh"> {m.name}</span>
            </button>
            <button type="button" disabled={busy !== ''} onClick={() => void exportIt(m)}>
              Export<span className="vh"> {m.name}</span>
            </button>
            {!mayChange && <ReasonTip label={'Why you cannot change ' + m.name} text={refusal} />}
          </span>
          {asking === m.id && (
            <span className="imw-ask" role="group" aria-label={'Delete ' + m.name}>
              <span>{deleteAsk(m)}</span>
              <button type="button" ref={keep('delete-yes:' + m.id)} className="danger" disabled={busy !== ''} onClick={() => void remove(m)}>
                {busy === m.id + 'delete' ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                disabled={busy !== ''}
                onClick={() => {
                  setAsking('')
                  focusLater(byKey('delete:' + m.id))
                }}
              >
                Keep it
              </button>
            </span>
          )}
          {said?.id === m.id && <ImportError error={said.error} />}
        </>
      ),
    },
  ]

  return (
    <DataTable
      caption="Saved mappings: the column choices, value answers and duplicate rule a file was matched with, under a name."
      columns={columns}
      rows={mappings}
      rowKey={(m) => m.id}
      defaultSort={{ key: 'name', dir: 'asc' }}
      empty={{
        title: 'No mapping is saved yet',
        detail: 'Tick "Remember this mapping" when you match the columns, and the next file with the same columns arrives matched.',
      }}
      footer={<ListCount shown={mappings.length} noun="saved mappings" />}
    />
  )
}

// ------------------------------------------------------------- the page

export function Imports() {
  const mayReadMappings = can('vendor.import') || can('config.read')
  const [tab, setTab] = useState<HistoryTab>(() =>
    historyTab(typeof window === 'undefined' ? '' : window.location.hash, mayReadMappings),
  )
  const [batches, setBatches] = useState<ImportBatch[] | null>(null)
  const [drafts, setDrafts] = useState<{ drafts: ImportDraftSummary[]; keptForDays: number } | null>(null)
  const [mappings, setMappings] = useState<ImportMappingListed[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const toast = useToast()

  // What a person does with an import after it was brought in (release 3.2):
  // undo it inside its window, restore an undo, or add its contracts and
  // MSAs. One at a time, opened below the list. Carrying on a commit that
  // stopped part way is one press, and says what it did in the same place.
  const [after, setAfter] = useState<{ id: string; kind: 'undo' | 'restore' | 'docs' } | null>(null)
  const [said, setSaid] = useState<
    { id: string; undo?: ImportUndoResult; restore?: ImportRestoreResult; undoUntil?: string } | null
  >(null)
  const [resume, setResume] = useState<{ id: string; busy: boolean; text?: string; error?: unknown } | null>(null)

  const loadTop = useCallback(() => {
    api
      .imports()
      .then((b) => {
        setBatches(b.imports)
        setError(null)
      })
      .catch(setError)
    api
      .importDrafts()
      .then(setDrafts)
      .catch((err) => {
        // Said, never shown as "nothing in progress".
        setDrafts({ drafts: [], keptForDays: 14 })
        setError(err)
      })
  }, [])
  const loadMappings = useCallback(() => {
    if (!mayReadMappings) {
      setMappings([])
      return
    }
    api
      .importMappings()
      .then((m) => setMappings(m.mappings))
      .catch(setError)
  }, [mayReadMappings])

  useEffect(loadTop, [loadTop])
  useEffect(loadMappings, [loadMappings])

  // The tab follows the address: Back and a link land on the part they name.
  useEffect(() => {
    const sync = () => {
      if (window.location.hash.startsWith('#/imports')) setTab(historyTab(window.location.hash, mayReadMappings))
    }
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [mayReadMappings])

  function openTab(t: HistoryTab) {
    setTab(t)
    if (window.location.hash !== TAB_ADDRESS[t]) window.location.replace(TAB_ADDRESS[t])
  }

  // While an import is being brought in, the list is read again every 3
  // seconds, so it moves on to what the import did (or to "Stopped part way")
  // without a reload. The reads stop when nothing is being brought in, and
  // when the page goes away.
  const running = (batches ?? []).some((b) => b.status === 'COMMITTING' && !b.canResume && !b.undoneAt)
  useEffect(() => {
    if (!running) return
    let live = true
    const t = setTimeout(() => {
      api
        .imports()
        .then((b) => {
          if (live) setBatches(b.imports)
        })
        .catch((err) => {
          if (live) setError(err)
        })
    }, FOLLOW_MS)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [running, batches])

  const afterBatch = after ? (batches?.find((b) => b.id === after.id) ?? null) : null
  const saidBatch = said ? (batches?.find((b) => b.id === said.id) ?? null) : null
  const resumeBatch = resume ? (batches?.find((b) => b.id === resume.id) ?? null) : null
  const inProgress = new Map((drafts?.drafts ?? []).map((d) => [d.importId, d]))

  function openAfter(id: string, kind: AfterKind) {
    setSaid(null)
    if (kind === 'resume') {
      setAfter(null)
      void carryOn(id)
      return
    }
    setResume(null)
    setAfter({ id, kind })
  }

  // Carries on a commit that stopped part way: the same commit again, which
  // picks up at the first row it had not reached. A large rest is written in
  // the background (202), and the list follows it.
  async function carryOn(id: string) {
    setResume({ id, busy: true })
    try {
      const r = await api.commitImport(id)
      const text = commitAccepted(r) ? importingWords((r.done ?? 0) + (r.remaining ?? 0)) : resultHeadline(r)
      setResume({ id, busy: false, text })
      toast(text)
    } catch (err) {
      setResume({ id, busy: false, error: err })
    } finally {
      loadTop()
    }
  }

  const batchColumns: Column<ImportBatch>[] = [
    {
      key: 'file',
      header: 'File',
      sortValue: (b) => b.fileName,
      render: (b) => (
        <>
          <strong className="imw-hname">{b.fileName || 'Unnamed file'}</strong>
          <span className="cell-sub">{importSource(b)}</span>
        </>
      ),
    },
    { key: 'who', header: 'Who', sortValue: (b) => b.startedBy ?? '', render: (b) => b.startedBy || <span className="muted">Not known</span> },
    {
      key: 'when',
      header: 'When',
      sortValue: (b) => b.committedAt || b.createdAt || '',
      render: (b) => shortDate(b.committedAt || b.createdAt || ''),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (b) => batchStatus(b)?.word ?? b.status,
      render: (b) => {
        const s = batchStatus(b)
        return s ? <Status tone={s.tone}>{s.word}</Status> : <Status tone="neutral">{words(b.status || 'Unknown')}</Status>
      },
    },
    { key: 'total', header: 'Rows', numeric: true, sortValue: (b) => b.rowsTotal, render: (b) => b.rowsTotal },
    {
      key: 'committed',
      header: 'Added',
      numeric: true,
      sortValue: (b) => b.rowsCommitted,
      render: (b) => <span className="mono">{b.rowsCommitted}</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      numeric: true,
      sortValue: (b) => b.rowsUpdated ?? 0,
      render: (b) => <span className="mono">{b.rowsUpdated ?? 0}</span>,
    },
    {
      key: 'after',
      header: 'After the import',
      render: (b) => (
        <AfterImport b={b} onOpen={openAfter} working={resume?.id === b.id && resume.busy} draft={inProgress.get(b.id)} />
      ),
    },
  ]

  const mine = (drafts?.drafts ?? []).filter((d) => d.yours).length
  const tabs: PageTab<HistoryTab>[] = [
    { key: 'imports', label: 'Imports', count: batches?.length },
    {
      key: 'in-progress',
      label: 'In progress',
      count: drafts?.drafts.length,
      alert: mine > 0 ? plural(mine, 'is yours', 'are yours') : undefined,
    },
    ...(mayReadMappings ? [{ key: 'mappings' as const, label: 'Saved mappings', count: mappings?.length }] : []),
  ]

  return (
    <>
      <div className="p1-head">
        <div>
          <h1>Import history</h1>
        </div>
        {can('vendor.import') && (
          <div className="p1-actions">
            <a className="button primary" href="#/import" data-action="tprm.imports.a01">
              Import a file
            </a>
          </div>
        )}
      </div>

      {error != null && <ErrorBox error={error} what="Import history" />}

      <PageTabs label="Import history" tabs={tabs} value={tab} onChange={openTab}>
        {tab === 'imports' && (
          <section className="panel panel-flush" aria-label="Imports">
            {batches === null ? (
              <Loading what="the imports" />
            ) : (
              <DataTable
                caption="Files imported, with who brought them in, when, what they added and what can still be done."
                columns={batchColumns}
                rows={batches}
                rowKey={(b) => b.id}
                defaultSort={{ key: 'when', dir: 'desc' }}
                firstRows={25}
                empty={{
                  title: 'Nothing has been imported yet',
                  detail: 'Importing reads and checks a file, and writes nothing until you import it.',
                  action: can('vendor.import') ? { label: 'Import a file', href: '#/import' } : undefined,
                }}
                footer={<ListCount shown={batches.length} noun="imports" />}
              />
            )}
          </section>
        )}
        {tab === 'in-progress' && (
          <section className="panel panel-flush" aria-label="Imports in progress">
            {drafts === null ? (
              <Loading what="the imports in progress" />
            ) : (
              <InProgress drafts={drafts.drafts} keptForDays={drafts.keptForDays} onChanged={loadTop} />
            )}
          </section>
        )}
        {tab === 'mappings' && (
          <section className="panel panel-flush" aria-label="Saved mappings">
            {mappings === null ? <Loading what="the saved mappings" /> : <SavedMappings mappings={mappings} onChanged={loadMappings} />}
          </section>
        )}
      </PageTabs>

      {after && afterBatch && after.kind !== 'docs' && (
        <section className="panel" aria-labelledby="after-heading">
          <div className="p1-head" style={{ marginBottom: 0 }}>
            <h2 id="after-heading" style={{ margin: 0 }}>
              {after.kind === 'undo' ? 'Undo ' : 'Restore '}
              {afterBatch.fileName || 'this import'}
            </h2>
            <div className="p1-actions">
              <button type="button" onClick={() => setAfter(null)}>
                Close
              </button>
            </div>
          </div>
          {after.kind === 'undo' ? (
            <UndoForm
              importId={afterBatch.id}
              undoUntil={afterBatch.undoUntil}
              onDone={(r) => {
                setSaid({ id: afterBatch.id, undo: r, undoUntil: afterBatch.undoUntil })
                setAfter(null)
                loadTop()
              }}
              onCancel={() => setAfter(null)}
            />
          ) : (
            <RestoreForm
              importId={afterBatch.id}
              onDone={(r) => {
                setSaid({ id: afterBatch.id, restore: r })
                setAfter(null)
                loadTop()
              }}
              onCancel={() => setAfter(null)}
            />
          )}
        </section>
      )}
      {after && after.kind === 'docs' && <ImportDocuments importId={after.id} onClose={() => setAfter(null)} />}
      {resume && (
        <section className="panel" aria-label={'Resuming ' + (resumeBatch?.fileName || 'the import')}>
          <div className="p1-head" style={{ marginBottom: 0 }}>
            <h2 style={{ margin: 0 }}>{resumeBatch?.fileName || 'The import'}</h2>
            <div className="p1-actions">
              <button type="button" onClick={() => setResume(null)}>
                Close
              </button>
            </div>
          </div>
          {resume.busy && <p role="status">Resuming...</p>}
          {resume.text && (
            <div className="note" role="status" style={{ marginTop: 12 }}>
              {resume.text}
            </div>
          )}
          {resume.error != null && <ImportError error={resume.error} />}
        </section>
      )}
      {said && (
        <section className="panel" aria-label={'What happened to ' + (saidBatch?.fileName || 'the import')}>
          <div className="p1-head" style={{ marginBottom: 0 }}>
            <h2 style={{ margin: 0 }}>{saidBatch?.fileName || 'The import'}</h2>
            <div className="p1-actions">
              <button type="button" onClick={() => setSaid(null)}>
                Close
              </button>
            </div>
          </div>
          {said.undo && <UndoSaid result={said.undo} undoUntil={said.undoUntil} />}
          {said.restore && <RestoreSaid result={said.restore} />}
        </section>
      )}
    </>
  )
}

export type AfterKind = 'undo' | 'restore' | 'docs' | 'resume'

// What can still be done with an import once it was brought in: its count
// report, Undo inside its window (or Restore after an undo, inside the same
// window), and its contracts and MSAs. An import not brought in yet is in
// progress: its step, and Continue for a person who may import. One being
// brought in says so; one whose commit stopped part way can be carried on, or
// what came in undone. Undo, Restore, Carry on and attaching documents are
// refused (403) to a person without vendor.create even when they hold
// vendor.import (founder rule for 3.2), so they are shown disabled with the
// same refusal the API gives.
//
// Only an import made with the universal import (release 3.2) can be undone,
// restored or given its contracts in bulk, and only it has a count report:
// the API answers 409 for an older one, so an older one is offered none.
export function AfterImport({
  b,
  onOpen,
  working = false,
  draft,
}: {
  b: ImportBatch
  onOpen: (id: string, kind: AfterKind) => void
  working?: boolean
  draft?: ImportDraftSummary
}) {
  const universal = b.universal !== false
  const mayCreate = can('vendor.create')
  const refusal = mayCreate ? undefined : refusalReason('vendor.create')
  if (b.status === 'COMMITTING' && !b.undoneAt) {
    if (!b.canResume) return <span className="muted">Importing</span>
    const sofar = [
      (b.rowsCommitted ?? 0) > 0 ? b.rowsCommitted + ' added' : '',
      (b.rowsUpdated ?? 0) > 0 ? b.rowsUpdated + ' updated' : '',
    ]
      .filter(Boolean)
      .join(', ')
    return (
      <>
        <span className="cell-sub" style={{ display: 'block', marginBottom: 4 }}>
          Stopped part way{sofar ? ', ' + sofar + ' so far' : ''}
        </span>
        <span className="imw-batch-acts">
          {universal && <CountReportButton importId={b.id} />}
          <button
            type="button"
            data-action="tprm.imports.a02"
            disabled={working || !mayCreate}
            title={refusal}
            onClick={() => onOpen(b.id, 'resume')}
          >
            {working ? 'Resuming...' : 'Resume'}
            <span className="vh"> importing {b.fileName}</span>
          </button>
          {universal && (
            <button type="button" disabled={working || !mayCreate} title={refusal} onClick={() => onOpen(b.id, 'undo')}>
              Undo what came in<span className="vh"> from {b.fileName}</span>
            </button>
          )}
          {!mayCreate && <ReasonTip label={'Why you cannot change ' + (b.fileName || 'this import')} text={refusal} />}
        </span>
      </>
    )
  }
  const brought = b.status === 'COMMITTED' || !!b.undoneAt
  if (!brought) {
    if (!draft) return <span className="muted">Not imported</span>
    return (
      <>
        <span className="cell-sub" style={{ display: 'block', marginBottom: 4 }}>
          In progress, {stepWords(draft).replace(/^Step/, 'step')}
          {draft.yours ? '' : ', ' + draft.ownerName + "'s"}
        </span>
        {can('vendor.import') && (
          <a className="button" href={importAddress(b.id, draft.stepNumber)}>
            {draft.yours ? 'Continue' : 'Open'}
            <span className="vh"> {b.fileName}</span>
          </a>
        )}
      </>
    )
  }
  if (!universal) {
    return <span className="muted">Imported{b.committedAt ? ' on ' + shortDate(b.committedAt) : ''}</span>
  }
  const windowOpen = !b.undoUntil || new Date(b.undoUntil).getTime() > Date.now()
  const state = b.undoneAt
    ? 'Undone on ' + shortDate(b.undoneAt)
    : b.canUndo && b.undoUntil
      ? 'You can undo this until ' + shortDate(b.undoUntil)
      : ''
  const more = [
    (b.rowsUpdated ?? 0) > 0 ? b.rowsUpdated + ' updated' : '',
    (b.documents ?? 0) > 0 ? b.documents + (b.documents === 1 ? ' document' : ' documents') : '',
  ]
    .filter(Boolean)
    .join(', ')
  return (
    <>
      {(state || more) && (
        <span className="cell-sub" style={{ display: 'block', marginBottom: 4 }}>
          {[state, more].filter(Boolean).join(', ')}
        </span>
      )}
      <span className="imw-batch-acts">
        <CountReportButton importId={b.id} />
        {b.canUndo && !b.undoneAt && (
          <button type="button" disabled={!mayCreate} title={refusal} onClick={() => onOpen(b.id, 'undo')}>
            Undo this import<span className="vh"> {b.fileName}</span>
          </button>
        )}
        {b.undoneAt && windowOpen && (
          <button type="button" disabled={!mayCreate} title={refusal} onClick={() => onOpen(b.id, 'restore')}>
            Restore<span className="vh"> {b.fileName}</span>
          </button>
        )}
        {!b.undoneAt && (
          <button type="button" disabled={!mayCreate} title={refusal} onClick={() => onOpen(b.id, 'docs')}>
            Add contracts and MSAs<span className="vh"> for {b.fileName}</span>
          </button>
        )}
        {!mayCreate && (!b.undoneAt || windowOpen) && (
          <ReasonTip label={'Why you cannot change ' + (b.fileName || 'this import')} text={refusal} />
        )}
      </span>
    </>
  )
}

